import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BadgeCheck,
  CalendarCheck,
  Clock,
  Download,
  KeyRound,
  Loader2,
  LogOut,
  RefreshCw,
  UserPlus,
} from "lucide-react";
import { api, formatINR } from "@/lib/api";
import ChangePasswordModal from "@/components/ChangePasswordModal";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3.5 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 pt-28" data-testid="admin-login-view">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        className="card-glow w-full max-w-md rounded-2xl p-8 sm:p-10"
        data-testid="admin-login-form"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
          <KeyRound size={20} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold uppercase text-white">Owner Login</h1>
        <p className="mt-2 text-sm text-neutral-400">Sign in to view all ISHU pre-bookings.</p>

        <div className="mt-7 space-y-4">
          <div>
            <label htmlFor="admin-email" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-login-email-input"
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@ishumotors.com"
              required
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-login-password-input"
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#e6b800] px-6 py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />}
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <div className="mt-5 flex items-center justify-between text-xs">
          <Link to="/admin/forgot" data-testid="login-forgot-link" className="text-neutral-400 transition-colors hover:text-[#e6b800]">
            Password भूल गए?
          </Link>
          <Link to="/admin/signup" data-testid="login-signup-link" className="font-semibold text-[#e6b800] transition-colors hover:text-[#ffc700]">
            नया owner account →
          </Link>
        </div>
      </motion.form>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [bookings, setBookings] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [tForm, setTForm] = useState({ name: "", email: "", password: "" });
  const [tLoading, setTLoading] = useState(false);
  const [cForm, setCForm] = useState({ email: "", password: "" });
  const [cLoading, setCLoading] = useState(false);
  const adminInputCls =
    "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-3.5 py-2.5 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

  const setT = (k) => (e) => setTForm((f) => ({ ...f, [k]: e.target.value }));
  const setC = (k) => (e) => setCForm((f) => ({ ...f, [k]: e.target.value }));

  const loadTeam = () => {
    api
      .get("/admin/team", { withCredentials: true })
      .then((r) => setTeam(r.data))
      .catch(() => setTeam([]));
  };

  const addMember = async (e) => {
    e.preventDefault();
    setTLoading(true);
    try {
      await api.post("/admin/team", tForm, { withCredentials: true });
      toast.success(`${tForm.email} को admin बना दिया`);
      setTForm({ name: "", email: "", password: "" });
      loadTeam();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setTLoading(false);
    }
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/admin/team/${m.id}`, { withCredentials: true });
      toast.success(`${m.email} हटा दिया`);
      loadTeam();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const resetCustomerPassword = async (e) => {
    e.preventDefault();
    setCLoading(true);
    try {
      await api.post("/admin/reset-customer-password", cForm, { withCredentials: true });
      toast.success(`${cForm.email} का password set हो गया`);
      setCForm({ email: "", password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setCLoading(false);
    }
  };

  const loadBookings = () => {
    api
      .get("/admin/bookings", { withCredentials: true })
      .then((r) => setBookings(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
  };

  useEffect(() => {
    loadBookings();
    loadTeam();
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/bookings/export", {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ishu-bookings.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bookings exported as CSV");
    } catch (e) {
      toast.error("Export failed. Try again.");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {}, { withCredentials: true });
    } catch (e) {
      /* ignore */
    }
    onLogout();
  };

  const total = bookings?.length ?? 0;
  const confirmed = bookings?.filter((b) => b.status === "confirmed").length ?? 0;
  const drives = bookings?.filter((b) => b.test_drive).length ?? 0;

  const todayStr = new Date().toDateString();
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const todayCount = bookings?.filter((b) => new Date(b.created_at).toDateString() === todayStr).length ?? 0;
  const weekCount = bookings?.filter((b) => new Date(b.created_at).getTime() >= weekAgo).length ?? 0;

  const stats = [
    { label: "Today's bookings", value: todayCount, id: "today" },
    { label: "This week", value: weekCount, id: "week" },
    { label: "Total bookings", value: total, id: "total" },
    { label: "Token paid", value: confirmed, id: "confirmed" },
    { label: "Pending payment", value: total - confirmed, id: "pending" },
    { label: "Test drives", value: drives, id: "drives" },
  ];

  const daily = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    const key = d.toDateString();
    return {
      key,
      label: `${d.getDate()}/${d.getMonth() + 1}`,
      count: bookings?.filter((b) => new Date(b.created_at).toDateString() === key).length ?? 0,
    };
  });
  const maxDaily = Math.max(1, ...daily.map((d) => d.count));

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Owner Dashboard</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold uppercase text-white sm:text-4xl">
            All pre-bookings
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#e6b800]/50 bg-[#e6b800]/10 px-5 py-2.5 text-sm font-semibold text-[#e6b800] transition-all duration-200 hover:bg-[#e6b800] hover:text-[#070709]"
          >
            <Download size={15} /> Export CSV
          </button>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="admin-change-password-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#e6b800]/60 hover:text-[#e6b800]"
          >
            <KeyRound size={15} /> Change password
          </button>
          <button
            onClick={loadBookings}
            data-testid="admin-refresh-button"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-neutral-300 transition-colors hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            aria-label="Refresh bookings"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#e6b800]/60 hover:text-[#e6b800]"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      <p className="mt-2 text-sm text-neutral-500" data-testid="admin-signed-in-as">
        Signed in as {user?.email}
      </p>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.id} className="rounded-2xl border border-white/10 bg-[#111116] p-5" data-testid={`admin-stat-${s.id}`}>
            <p className="text-gold-gradient font-display text-3xl font-extrabold">{s.value}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0e] p-6" data-testid="admin-team-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#e6b800]">
            Team · {team.length} admin{team.length === 1 ? "" : "s"}
          </p>
          <button
            onClick={() => setTeamOpen((v) => !v)}
            data-testid="team-toggle-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#e6b800]/50 bg-[#e6b800]/10 px-4 py-2 text-xs font-semibold text-[#e6b800] transition-colors duration-200 hover:bg-[#e6b800] hover:text-[#070709]"
          >
            <UserPlus size={13} /> {teamOpen ? "बंद करें" : "Team member जोड़ें"}
          </button>
        </div>

        {teamOpen && (
          <form onSubmit={addMember} className="mt-5 grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-[#111116] p-4 sm:grid-cols-4" data-testid="team-add-form">
            <input data-testid="team-name-input" className={adminInputCls} placeholder="नाम" value={tForm.name} onChange={setT("name")} minLength={2} required />
            <input data-testid="team-email-input" type="email" className={adminInputCls} placeholder="email@ishumotors.com" value={tForm.email} onChange={setT("email")} required />
            <input data-testid="team-password-input" type="password" className={adminInputCls} placeholder="Password (min 8)" value={tForm.password} onChange={setT("password")} minLength={8} required />
            <button type="submit" disabled={tLoading} data-testid="team-add-button" className="rounded-full bg-[#e6b800] px-4 py-2.5 text-sm font-bold text-[#070709] transition-colors duration-200 hover:bg-[#ffc700] disabled:opacity-50">
              {tLoading ? "Adding..." : "Add admin"}
            </button>
          </form>
        )}

        <div className="mt-5 space-y-2.5" data-testid="team-list">
          {team.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#111116] px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">
                  {m.name} {m.email === user?.email && <span className="ml-1 text-[10px] text-[#e6b800]">(आप)</span>}
                </p>
                <p className="truncate text-[11px] text-neutral-500">{m.email}</p>
              </div>
              {m.email !== user?.email && (
                <button
                  onClick={() => removeMember(m)}
                  data-testid={`team-remove-${m.email}`}
                  className="rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-neutral-400 transition-colors duration-200 hover:border-red-500/60 hover:text-red-400"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0e] p-6" data-testid="admin-customer-reset-section">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#e6b800]">Customer password reset</p>
        <p className="mt-2 text-xs leading-relaxed text-neutral-500">
          Customer का password directly set करें — कोई email नहीं जाएगा। Customer नए password से login करेगा।
        </p>
        <form onSubmit={resetCustomerPassword} className="mt-4 grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-[#111116] p-4 sm:grid-cols-3" data-testid="customer-reset-form">
          <input data-testid="customer-reset-email-input" type="email" className={adminInputCls} placeholder="customer@example.com" value={cForm.email} onChange={setC("email")} required />
          <input data-testid="customer-reset-new-password-input" type="password" className={adminInputCls} placeholder="नया password (min 8)" value={cForm.password} onChange={setC("password")} minLength={8} required />
          <button type="submit" disabled={cLoading} data-testid="customer-reset-button" className="rounded-full bg-[#e6b800] px-4 py-2.5 text-sm font-bold text-[#070709] transition-colors duration-200 hover:bg-[#ffc700] disabled:opacity-50">
            {cLoading ? "Setting..." : "Password set करें"}
          </button>
        </form>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0e] p-6" data-testid="admin-daily-chart">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#e6b800]">
          Daily bookings · पिछले 14 दिन
        </p>
        <div className="mt-6 flex h-36 items-end gap-1.5 sm:gap-2.5" data-testid="daily-chart-bars">
          {daily.map((d) => (
            <div key={d.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
              <span className="mb-1 font-mono2 text-[10px] text-neutral-400">{d.count || ""}</span>
              <div
                className={`w-full rounded-t-md transition-all duration-300 ${d.count ? "bg-[#e6b800]" : "bg-neutral-800/80"}`}
                style={{ height: d.count ? `${Math.max(8, (d.count / maxDaily) * 100)}%` : "4px" }}
                title={`${d.label}: ${d.count}`}
              />
              <span className="mt-2 text-[9px] text-neutral-600">{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 overflow-hidden rounded-2xl border border-white/10" data-testid="admin-bookings-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#0a0a0e] font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <th className="px-5 py-4">Reference</th>
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Variant</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Founding</th>
                <th className="px-5 py-4">Token</th>
                <th className="px-5 py-4">Payment</th>
                <th className="px-5 py-4">Test drive</th>
                <th className="px-5 py-4">Booked on</th>
              </tr>
            </thead>
            <tbody>
              {(bookings ?? []).map((b) => (
                <tr key={b.id} className="border-b border-white/5 text-neutral-300 transition-colors hover:bg-white/[0.03]" data-testid={`admin-booking-row-${b.reference}`}>
                  <td className="px-5 py-4 font-mono2 text-[12px] text-[#e6b800]">{b.reference}</td>
                  <td className="px-5 py-4 font-semibold text-white">{b.name}</td>
                  <td className="px-5 py-4 text-neutral-400" data-testid={`admin-variant-${b.reference}`}>
                    {b.model ? `${b.model} ` : ""}{b.variant || "ONE"}{b.color ? ` · ${b.color}` : ""}
                  </td>
                  <td className="px-5 py-4">
                    <p>{b.email}</p>
                    <p className="text-xs text-neutral-500">{b.phone}</p>
                  </td>
                  <td className="px-5 py-4">
                    {b.status === "confirmed" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 px-3 py-1 text-[11px] font-semibold text-[#e6b800]">
                        <BadgeCheck size={12} /> Confirmed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-[11px] text-neutral-400">
                        <Clock size={12} /> Pending
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">{b.status === "confirmed" ? formatINR(b.token_amount) : "—"}</td>
                  <td className="px-5 py-4" data-testid={`admin-payment-${b.reference}`}>
                    {b.payment_method ? (
                      <span className="font-mono2 text-[11px] text-neutral-400">
                        {b.payment_method === "razorpay" ? "Razorpay" : b.payment_method}
                        {b.payment_id ? ` · …${b.payment_id.slice(-8)}` : ""}
                      </span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4" data-testid={`admin-founding-${b.reference}`}>
                    {b.founding_number ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 px-3 py-1 text-[11px] font-bold text-[#e6b800]">
                        <BadgeCheck size={12} /> #{b.founding_number}
                      </span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {b.test_drive ? (
                      <span className="inline-flex items-center gap-1.5 text-[#e6b800]" data-testid={`admin-testdrive-${b.reference}`}>
                        <CalendarCheck size={13} /> {b.test_drive.city}, {b.test_drive.date}, {b.test_drive.slot}
                      </span>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs text-neutral-500">
                    {new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                </tr>
              ))}
              {bookings?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-neutral-500">
                    No bookings yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {bookings === null && (
          <div className="flex items-center justify-center gap-2 py-12 text-neutral-500">
            <Loader2 size={16} className="animate-spin" /> Loading bookings…
          </div>
        )}
      </div>

      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState("checking");

  useEffect(() => {
    api
      .get("/auth/me", { withCredentials: true })
      .then((r) => setUser(r.data?.role === "admin" ? r.data : false))
      .catch(() => setUser(false));
  }, []);

  if (user === "checking") {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-28 text-neutral-500" data-testid="admin-loading">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (!user) return <Login onLogin={(u) => setUser(u)} />;
  return <Dashboard user={user} onLogout={() => setUser(false)} />;
}